import React from 'react';
import { Link } from 'react-router-dom';

import Button from '../ui/Button';

/**
 * Reusable layout for detail pages (election, county, district, authority)
 * Provides consistent structure: breadcrumb → hero → sections → error states
 *
 * @param {object} props
 * @param {string} props.title - Page title
 * @param {array} props.breadcrumbs - Breadcrumb navigation
 *   Each: { label, url } or { label } for current page
 * @param {React.ReactNode} props.hero - Hero section content
 * @param {array} props.sections - Content sections
 *   Each: { content, className? }
 * @param {boolean} props.isLoading - Loading state
 * @param {object} props.error - Error object
 * @param {function} props.onRetry - Retry function
 * @param {string} props.containerClassName - Container width class (default: max-w-3xl)
 */
export default function DetailPageLayout({
  breadcrumbs = [],
  hero = null,
  sections = [],
  isLoading = false,
  error = null,
  onRetry = null,
  containerClassName = 'max-w-3xl',
}) {
  // Loading State
  if (isLoading) {
    return (
      <div className="bg-neutral-50 min-h-screen py-8">
        <div className={`container-custom ${containerClassName}`}>
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-neutral-200 rounded w-1/4" />
            <div className="h-10 bg-neutral-200 rounded w-3/4" />
            <div className="h-40 bg-neutral-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="bg-neutral-50 min-h-screen py-8">
        <div className={`container-custom ${containerClassName} text-center py-16`}>
          <p className="text-neutral-600 mb-4">
            {error instanceof Error ? error.message : 'Item not found.'}
          </p>
          {onRetry ? (
            <Button variant="outline" onClick={onRetry}>
              Try Again
            </Button>
          ) : (
            <Button variant="outline" onClick={() => window.history.back()}>
              Go Back
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-50 min-h-screen py-8">
      <div className={`container-custom ${containerClassName}`}>
        {/* Breadcrumb Navigation */}
        {breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-1.5 text-sm text-neutral-500 mb-6">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={idx}>
                {crumb.url ? (
                  <Link to={crumb.url} className="hover:text-primary-600 transition-colors">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-neutral-800 truncate">{crumb.label}</span>
                )}
                {idx < breadcrumbs.length - 1 && <span>/</span>}
              </React.Fragment>
            ))}
          </nav>
        )}

        {/* Hero Section */}
        {hero && (
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 mb-5">
            {hero}
          </div>
        )}

        {/* Content Sections */}
        {sections.map((section, idx) => (
          <div
            key={idx}
            className={`bg-white rounded-2xl border border-neutral-200 shadow-sm p-5 ${
              idx < sections.length - 1 ? 'mb-5' : ''
            } ${section.className || ''}`}
          >
            {section.title && (
              <h2 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-3">
                {section.title}
              </h2>
            )}
            {section.content}
          </div>
        ))}
      </div>
    </div>
  );
}
